import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Link2, Plus, X } from 'lucide-react';

import { CompanyAPI } from '../../services/api';
import { formatDate, todayIso } from './selloutUtils';

const receiveNoteLabel = (note) => {
  if (!note) return '';
  const warehouse = note.logical_warehouse?.name || note.legacy_warehouse_text || note.warehouse || '';
  return `NR ${note.number}${warehouse ? ` | ${warehouse}` : ''}${note.date ? ` | ${formatDate(note.date)}` : ''}`;
};

export default function SellOutReceiveNoteLink({
  selectedNote,
  onChange,
  disabled,
}) {
  const [notes, setNotes] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [newNote, setNewNote] = useState({
    number: '',
    date: todayIso(),
    legacy_warehouse_text: '',
  });

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    CompanyAPI.receiveNotes.list({
      search: search.trim() || undefined,
      is_active: true,
      ordering: '-created_at',
    })
      .then(({ data }) => {
        if (!mounted) return;
        const rows = Array.isArray(data) ? data : data?.results || [];
        setNotes(rows);
      })
      .catch(() => {
        if (mounted) {
          setNotes([]);
          setError('No se pudieron cargar notas de entrada.');
        }
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [search]);

  const options = useMemo(() => {
    if (!selectedNote?.id) return notes;
    return [selectedNote, ...notes.filter((note) => note.id !== selectedNote.id)];
  }, [notes, selectedNote]);

  const setNewValue = (key, value) => {
    setNewNote((prev) => ({ ...prev, [key]: value }));
  };

  const createNote = async () => {
    setError('');
    const number = Number(newNote.number);
    const warehouse = String(newNote.legacy_warehouse_text || '').trim();
    if (!Number.isFinite(number) || number <= 0) {
      setError('Numero de nota de entrada requerido.');
      return;
    }
    if (!warehouse) {
      setError('Referencia de bodega requerida para crear la nota.');
      return;
    }

    setSaving(true);
    try {
      const { data } = await CompanyAPI.receiveNotes.create({
        number,
        date: newNote.date || todayIso(),
        legacy_warehouse_text: warehouse,
        warehouse,
      });
      onChange(data);
      setNotes((prev) => [data, ...prev.filter((note) => note.id !== data.id)]);
      setNewNote({ number: '', date: todayIso(), legacy_warehouse_text: '' });
    } catch (err) {
      const detail = err?.response?.data;
      const message = typeof detail === 'string'
        ? detail
        : detail?.detail || detail?.warehouse || detail?.non_field_errors?.join?.(' ') || 'No se pudo crear la nota de entrada.';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="subtitle1" fontWeight={700}>Nota de entrada</Typography>
        <Typography variant="body2" color="text.secondary">
          Vincule una nota existente o cree una referencia nueva para este documento Sell Out.
        </Typography>
      </Box>

      {error ? <Alert severity="error">{error}</Alert> : null}

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
        <Autocomplete
          fullWidth
          options={options}
          value={selectedNote}
          loading={loading}
          disabled={disabled}
          onInputChange={(_, value) => setSearch(value)}
          onChange={(_, note) => onChange(note || null)}
          getOptionLabel={receiveNoteLabel}
          isOptionEqualToValue={(option, value) => option?.id === value?.id}
          renderInput={(params) => <TextField {...params} size="small" label="Nota existente" />}
        />
        <Button
          variant="outlined"
          color="inherit"
          startIcon={<X size={16} />}
          onClick={() => onChange(null)}
          disabled={disabled || !selectedNote}
        >
          Desvincular
        </Button>
      </Stack>

      <Divider />

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
        <TextField
          size="small"
          type="number"
          label="Nueva NR"
          value={newNote.number}
          disabled={disabled || saving}
          onChange={(event) => setNewValue('number', event.target.value)}
          inputProps={{ min: 1 }}
          sx={{ minWidth: { md: 150 } }}
        />
        <TextField
          size="small"
          type="date"
          label="Fecha"
          value={newNote.date}
          disabled={disabled || saving}
          onChange={(event) => setNewValue('date', event.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: { md: 160 } }}
        />
        <TextField
          size="small"
          label="Referencia bodega"
          value={newNote.legacy_warehouse_text}
          disabled={disabled || saving}
          onChange={(event) => setNewValue('legacy_warehouse_text', event.target.value)}
          sx={{ flex: 1, minWidth: { md: 220 } }}
        />
        <Button
          variant="contained"
          startIcon={selectedNote ? <Link2 size={16} /> : <Plus size={16} />}
          onClick={createNote}
          disabled={disabled || saving}
        >
          Crear y vincular
        </Button>
      </Stack>
    </Stack>
  );
}
